import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './SupplierPayments.css';

const ClientPayments = ({ itineraryId, totalAmount, onPaymentsUpdate }) => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [sendingReminder, setSendingReminder] = useState(false);

  const [formData, setFormData] = useState({
    payment_date: new Date().toISOString().split('T')[0],
    amount: '',
    payment_type: 'partial',
    payment_method: '',
    reference_number: '',
    notes: ''
  });

  const [reminderData, setReminderData] = useState({
    custom_message: ''
  });

  useEffect(() => {
    if (itineraryId) {
      fetchPayments();
    }
  }, [itineraryId]);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `/api/itineraries/${itineraryId}/client-payments`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPayments(response.data);
      if (onPaymentsUpdate) {
        onPaymentsUpdate(response.data);
      }
    } catch (err) {
      console.error('Error fetching client payments:', err);
      setError('Failed to load client payments');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setFormData({
      payment_date: new Date().toISOString().split('T')[0],
      amount: '',
      payment_type: 'partial',
      payment_method: '',
      reference_number: '',
      notes: ''
    });
  };

  const handleAddPayment = () => {
    resetForm();
    setShowAddModal(true);
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setError('');
    setSuccess('');

    if (!formData.payment_date || !formData.amount || !formData.payment_method) {
      setError('Payment date, amount, and payment method are required');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `/api/itineraries/${itineraryId}/client-payments`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSuccess(`Payment recorded successfully! Remaining balance: $${response.data.itinerary_summary.remaining_balance.toFixed(2)}`);
      await fetchPayments();
      setShowAddModal(false);
      resetForm();
      
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      console.error('Error recording payment:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Failed to record payment';
      setError(errorMessage);
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('Are you sure you want to delete this payment record?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `/api/itineraries/${itineraryId}/client-payments/${paymentId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSuccess('Payment deleted successfully');
      await fetchPayments();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error deleting payment:', err);
      setError('Failed to delete payment');
    }
  };

  const handleSendReminder = async (e) => {
    e.preventDefault();
    setSendingReminder(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `/api/itineraries/${itineraryId}/send-balance-reminder`,
        reminderData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSuccess(`Balance reminder sent successfully to ${response.data.sent_to}`);
      setShowReminderModal(false);
      setReminderData({ custom_message: '' });
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      console.error('Error sending reminder:', err);
      const errorMessage = err.response?.data?.error || 'Failed to send reminder';
      setError(errorMessage);
    } finally {
      setSendingReminder(false);
    }
  };

  const totalPaid = payments.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
  const remainingBalance = parseFloat(totalAmount || 0) - totalPaid;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="supplier-payments-section">
      <div className="section-header">
        <div>
          <h3>Client Payments Received</h3>
          <p className="section-description">
            Track payments received from clients (cash, check, wire transfer, etc.)
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button 
            type="button" 
            className="btn btn-secondary" 
            onClick={() => setShowReminderModal(true)}
            disabled={!totalAmount || totalPaid >= totalAmount}
          >
            📧 Send Balance Reminder
          </button>
          <button type="button" className="btn btn-primary" onClick={handleAddPayment}>
            + Record Payment
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-error">
          {error}
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          {success}
        </div>
      )}

      {/* Payment Summary */}
      <div className="payment-summary">
        <div className="summary-card">
          <div className="summary-label">Total Itinerary Cost</div>
          <div className="summary-value">{formatCurrency(totalAmount || 0)}</div>
        </div>
        <div className="summary-card">
          <div className="summary-label">Total Received from Client</div>
          <div className="summary-value paid">{formatCurrency(totalPaid)}</div>
        </div>
        <div className="summary-card">
          <div className="summary-label">Client Balance Due</div>
          <div className={`summary-value ${remainingBalance > 0 ? 'remaining' : 'complete'}`}>
            {formatCurrency(remainingBalance)}
          </div>
        </div>
      </div>

      {/* Payments List */}
      {loading ? (
        <div className="loading-state">Loading payments...</div>
      ) : payments.length === 0 ? (
        <div className="empty-state">
          <p>No client payments recorded yet.</p>
          <p className="empty-state-hint">
            Click "Record Payment" to track payments received from your client.
          </p>
        </div>
      ) : (
        <div className="payments-table-container">
          <table className="payments-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Amount</th>
                <th>Type</th>
                <th>Method</th>
                <th>Reference</th>
                <th>Notes</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((payment) => (
                <tr key={payment.id}>
                  <td>{formatDate(payment.processed_at)}</td>
                  <td className="amount-cell">{formatCurrency(payment.amount)}</td>
                  <td>{payment.payment_type || '-'}</td>
                  <td>{payment.payment_method || '-'}</td>
                  <td>{payment.confirmation_number || '-'}</td>
                  <td className="notes-cell">
                    {payment.transaction_id ? (
                      <span className="notes-preview" title={payment.transaction_id}>
                        {payment.transaction_id.length > 30 
                          ? `${payment.transaction_id.substring(0, 30)}...` 
                          : payment.transaction_id}
                      </span>
                    ) : '-'}
                  </td>
                  <td className="actions-cell">
                    <button 
                      className="btn-icon btn-delete" 
                      onClick={() => handleDeletePayment(payment.id)}
                      title="Delete payment"
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Payment Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Record Client Payment</h3>
              <button className="close-button" onClick={() => setShowAddModal(false)}>×</button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="payment_date">Payment Date *</label>
                    <input
                      type="date"
                      id="payment_date"
                      name="payment_date"
                      value={formData.payment_date}
                      onChange={handleInputChange}
                      required
                      className="form-input"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="amount">Amount *</label>
                    <input
                      type="number"
                      id="amount"
                      name="amount"
                      value={formData.amount}
                      onChange={handleInputChange}
                      step="0.01"
                      min="0"
                      required
                      className="form-input"
                      placeholder="0.00"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="payment_type">Payment Type</label>
                    <select
                      id="payment_type"
                      name="payment_type"
                      value={formData.payment_type}
                      onChange={handleInputChange}
                      className="form-input"
                    >
                      <option value="deposit">Deposit</option>
                      <option value="partial">Partial Payment</option>
                      <option value="final">Final Payment</option>
                      <option value="full">Full Payment</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="payment_method">Payment Method *</label>
                    <select
                      id="payment_method"
                      name="payment_method"
                      value={formData.payment_method}
                      onChange={handleInputChange}
                      required
                      className="form-input"
                    >
                      <option value="">Select method...</option>
                      <option value="Cash">Cash</option>
                      <option value="Check">Check</option>
                      <option value="Wire Transfer">Wire Transfer</option>
                      <option value="ACH">ACH</option>
                      <option value="Credit Card">Credit Card</option>
                      <option value="Debit Card">Debit Card</option>
                      <option value="Zelle">Zelle</option>
                      <option value="Venmo">Venmo</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="reference_number">Reference Number</label>
                  <input
                    type="text"
                    id="reference_number"
                    name="reference_number"
                    value={formData.reference_number}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Check #, Confirmation #, Transaction ID, etc."
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="notes">Notes</label>
                  <textarea
                    id="notes"
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    rows="3"
                    className="form-input"
                    placeholder="Additional details about this payment..."
                  />
                </div>
              </div>

              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setShowAddModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Record Payment
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Send Reminder Modal */}
      {showReminderModal && (
        <div className="modal-overlay" onClick={() => setShowReminderModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Send Balance Reminder to Client</h3>
              <button className="close-button" onClick={() => setShowReminderModal(false)}>×</button>
            </div>

            <form onSubmit={handleSendReminder}>
              <div className="modal-body">
                <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                  <p style={{ margin: '0 0 10px 0', fontWeight: 'bold' }}>Email will include:</p>
                  <ul style={{ margin: 0, paddingLeft: '20px' }}>
                    <li>Total trip cost: {formatCurrency(totalAmount)}</li>
                    <li>Amount paid: {formatCurrency(totalPaid)}</li>
                    <li>Remaining balance: {formatCurrency(remainingBalance)}</li>
                    <li>Payment history</li>
                    <li>Your contact information</li>
                  </ul>
                </div>

                <div className="form-group">
                  <label htmlFor="custom_message">Custom Message (Optional)</label>
                  <textarea
                    id="custom_message"
                    name="custom_message"
                    value={reminderData.custom_message}
                    onChange={(e) => setReminderData({ custom_message: e.target.value })}
                    rows="4"
                    className="form-input"
                    placeholder="Add a personal message to your client (e.g., 'Final payment is due by March 15th...')"
                  />
                </div>
              </div>

              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setShowReminderModal(false)}
                  disabled={sendingReminder}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={sendingReminder}
                >
                  {sendingReminder ? 'Sending...' : 'Send Reminder'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientPayments;
